import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { GetInsurancePolicies } from '../../../api/insurancedetailApi';

// Interfaces
interface InsurancePolicy {
    policy_number: string;
    policy_type: string;
    policy_icon: string;
    product_document: string | null;
    start_date: string;
    end_date: string;
    issue_date: string;
    status: string;
    insured_person_name: string;
    insured_person_type: string;
    insured_person_pin_or_tin: string;
    insurance_amount: string;
    insurance_subject: string;
    premium: string;
    currency: string;
    has_printout: boolean;
}

interface InsurancePolicyState {
    policies: InsurancePolicy[];
    loading: boolean;
    error: string | null;
    message: string | null;
}

interface InsurancePolicyResponse {
    data: InsurancePolicy[];
    status: number;
    message?: string;
}

interface ErrorDetail {
    code: string;
    detail: string;
    attr: string | null;
}

interface StructuredErrorResponse {
    type: 'validation_error' | 'server_error';
    errors: ErrorDetail[];
}

interface GenericErrorResponse {
    error?: string;
}

// Initial state
const initialState: InsurancePolicyState = {
    policies: [],
    loading: false,
    error: null,
    message: null
};

// Async thunk
export const fetchInsurancePolicies = createAsyncThunk<
InsurancePolicyResponse,
    void,
{ rejectValue: string, state: any }
>(
    'insurancePolicy/fetchInsurancePolicies',
        async (_, thunkAPI) => {
            try {
                const state = thunkAPI.getState();
                const language = state.language?.currentLanguage || 'az';

                const result = await GetInsurancePolicies(language);

                // Success - status 200
                if (result.status === 200) {
                    const successData = result.data as any;
                    return {
                        data: successData.data || successData,
                        status: result.status,
                        message: successData.message || 'Uğurlu əməliyyat'
                    };
                }

                // Error handling
                const errorData = result.data;

                // Structured error
                if (errorData && typeof errorData === 'object' && 'type' in errorData && 'errors' in errorData) {
                    const structuredError = errorData as StructuredErrorResponse;
                    const firstError = structuredError.errors[0];
                    return thunkAPI.rejectWithValue(
                        firstError?.detail || 'Xəta baş verdi'
                    );
                }

                // Generic error
                if (errorData && typeof errorData === 'object' && 'error' in errorData) {
                    const genericError = errorData as GenericErrorResponse;
                    return thunkAPI.rejectWithValue(
                        genericError.error || 'Xəta baş verdi'
                    );
                }

                // Network error
                if (result.status === 0) {
                    return thunkAPI.rejectWithValue('Şəbəkə xətası - cavab alınmadı');
                }

                // Default error
                return thunkAPI.rejectWithValue('Gözlənilməz xəta baş verdi');

            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Məlumat əldə edilmədi';
                return thunkAPI.rejectWithValue(errorMessage);
            }
        },
        {
            condition: (_, { getState }) => {
                const { insurancePolicy } = getState() as any;
                if (insurancePolicy.loading) {
                    return false;
                }
            }
        }
);

// Slice with reducers
const insurancePolicySlice = createSlice({
    name: 'insurancePolicy',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.message = null;
        },
        resetInsurancePolicyState: (state) => {
            state.policies = [];
            state.loading = false;
            state.error = null;
            state.message = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInsurancePolicies.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(fetchInsurancePolicies.fulfilled, (state, action: PayloadAction<InsurancePolicyResponse>) => {
                state.loading = false;
                state.policies = action.payload.data;
                state.error = null;
                state.message = action.payload.message || null;
            })
            .addCase(fetchInsurancePolicies.rejected, (state, action) => {
                state.loading = false;
                state.policies = [];
                state.error = action.payload || 'Məlumat yüklənmədi';
                state.message = null;
            });
    }
});

export const selectPolicyByNumber = (state: any, policyNumber: string) => {
    return state.insurancePolicy.policies.find(
        (policy: InsurancePolicy) => policy.policy_number === policyNumber
    );
};
export const { clearError, resetInsurancePolicyState } = insurancePolicySlice.actions;
export default insurancePolicySlice.reducer;